import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { setTheme } from 'ngx-bootstrap/utils';
import { NgxSpinnerModule } from 'ngx-spinner';
import { KeycloakService } from 'keycloak-angular';
import { buildInfo } from '../environments/build-info';
import { AuthService } from './shared/services/auth-service.service';
import { ThemeService } from './shared/services/theme.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, NgxSpinnerModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  readonly buildInfo = buildInfo;

  private authService = inject(AuthService);
  private keycloakService = inject(KeycloakService);
  private visibilityHandler = () => this.revalidateSession('visibilitychange');
  private focusHandler = () => this.revalidateSession('window-focus');

  private isCheckingAuth = signal(true);
  /** Mostra layout principal após validação inicial (estilo mile-sga-front). */
  showMain = computed(() => this.authService.isAuthenticated() && !this.isCheckingAuth());

  renderer = inject(Renderer2);
  themeService = inject(ThemeService);

  get buildRevisionLabel(): string {
    const v = this.buildInfo.sourceVersion as string;
    if (v === 'local' || v.length < 8) {
      return v;
    }
    return v.slice(0, 7);
  }

  get buildStampTitle(): string {
    const b = this.buildInfo;
    return [
      `package.json: ${b.packageVersion}`,
      `Build: ${b.buildNumber} (id ${b.buildId})`,
      `Commit: ${b.sourceVersion}`,
      `Gerado em: ${b.builtAt}`,
    ].join('\n');
  }

  constructor() {
    setTheme('bs5');
  }

  ngOnInit(): void {
    this.checkInitialAuth();
    this.registerVisibilityListeners();
    /** Expiração do token: intervalo único em `AuthService.startTokenExpirationWatcher`. */
    this.themeService.theme.subscribe((theme: string) =>
      this.setTheme(theme as 'light' | 'dark')
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('focus', this.focusHandler);
  }

  setTheme(theme: 'light' | 'dark') {
    this.renderer.setAttribute(document.documentElement, 'data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }

  private checkInitialAuth(): void {
    this.isCheckingAuth.set(true);
    try {
      const kc = this.keycloakService.getKeycloakInstance();

      if (!kc.authenticated) {
        void this.forceLogin();
        return;
      }

      const token = kc.token;
      if (!token || this.authService.isTokenInvalid(token)) {
        console.warn('[AppComponent] Token inválido no arranque. Forçando login...');
        this.authService.user.set(null);
        void this.forceLogin();
        return;
      }

      this.authService.hydrateUserFromTokenSync();
    } catch (error) {
      console.error('Erro ao verificar autenticação inicial:', error);
      this.authService.user.set(null);
      void this.forceLogin();
    } finally {
      /** Duplo rAF: dá ao browser um paint do estado de loading antes de trocar para o shell principal (menos “piscar”). */
      const done = () => this.isCheckingAuth.set(false);
      if (typeof requestAnimationFrame === 'undefined') {
        done();
      } else {
        requestAnimationFrame(() => requestAnimationFrame(done));
      }
    }
  }

  /** Verificação local apenas — sem promessas Keycloak (evita loops em HML). */
  private checkTokenExpiration(): void {
    try {
      const kc = this.keycloakService.getKeycloakInstance();
      if (!kc.authenticated) {
        this.authService.user.set(null);
        void this.forceLogin();
        return;
      }

      const token = kc.token;
      if (!token || this.authService.isTokenInvalid(token)) {
        this.authService.user.set(null);
        this.authService.handleSessionExpired();
        return;
      }
    } catch (error) {
      console.error('Erro ao verificar expiração do token:', error);
      this.authService.user.set(null);
      this.authService.handleSessionExpired();
    }
  }

  private registerVisibilityListeners(): void {
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('focus', this.focusHandler);
  }

  private revalidateSession(source: string): void {
    if (source === 'visibilitychange' && document.hidden) {
      return;
    }
    this.checkTokenExpiration();
  }

  private async forceLogin(): Promise<void> {
    this.authService.user.set(null);
    try {
      await this.keycloakService.login({
        redirectUri: window.location.origin + window.location.pathname,
      });
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  }
}
